import {
  Copy16,
  CheckmarkOutline16,
  SailboatOffshore16,
} from '@carbon/icons-react';
import { faTelescope } from '@fortawesome/pro-duotone-svg-icons';
import { Tooltip, Typography } from 'antd';
import moment from 'moment';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { ProjectId, UserStatus } from 'api/models';
import { MembersMeta, Project } from 'api/project';
import { UserProfileInfo } from 'api/user';
import { ReactComponent as Crown } from 'assets/svgs/crown.svg';
import { Avatar } from 'components/atoms/avatar';
import { Button as StyledButton } from 'components/atoms/button';
import { currencyRenderer } from 'components/atoms/currency-renderer';
import { Menu } from 'components/atoms/dropdown';
import { EmptyCell } from 'components/atoms/empty-cell';
import {
  EmptyState,
  EmptyStateContainer,
  EmptyStateIcon,
} from 'components/atoms/empty-state';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import { GetTimeOffTooltipContent } from 'components/atoms/time-off-tooltip';
import { toastService } from 'components/molecules/toast';
import { MoreActions } from 'components/organisms/projects/project-settings/team-members/team-table/columns/more-actions';
import { PendingStatusText } from 'components/organisms/projects/project-settings/team-members/team-table/columns/status';
import { Text02, SingleLineEllipsis } from 'components/styles';
import { User } from 'data/members/api';
import { useProjectDetail } from 'data/projects';
import { useAccountCurrency } from 'hooks/use-account-currency';
import { useGetAllNativeUsersFromIndexedDb } from 'hooks/use-idb';
import { Translate, useTranslate } from 'hooks/use-translation';
import { Membership, Memberships } from 'stores/memberships';
import { fullName } from 'utils/full-name';
import { checkIfCurrentDateHasTimeOff } from 'utils/time-off-utils';
import { TeamMembersMoreActionsMenu } from './more-actions-menu/more-actions-menu';
import type { MemberCellRendererParams } from './table-utils';

interface UserData extends User {
  user: User;
}

const { Text } = Typography;

const FlexCenter = css`
  display: flex !important;
  justify-content: center;
  align-items: center;
`;

const StyledText = styled(Text)`
  .ant-typography-copy {
    margin-left: 0;
    ${FlexCenter};
  }
`;

const SecondaryText = styled.span`
  width: 100%;
  ${Text02};
`;
const NameWrapper = styled.div`
  margin: 0;
  padding: 0;
  max-width: 200px;
  ${SingleLineEllipsis};
`;

const MailWrapper = styled.div`
  margin: 0;
  padding: 0;
  max-width: 100%;
  ${SingleLineEllipsis};
`;

const ClickableName = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const AvatarWrapper = styled.span`
  margin-right: var(--spacing-03);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const CopyIcon = styled(Copy16)`
  color: var(--black-100);
`;

const CopySuccessIcon = styled(CheckmarkOutline16)`
  color: var(--success-color);
`;

const Button = styled(StyledButton)`
  padding: 0px;
  background: transparent;
  visibility: hidden;
  transition: opacity 1s ease;
  width: var(--input-size-sm);
  ${FlexCenter};
`;

const MailContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  &:hover ${Button} {
    visibility: visible;
  }
`;

export const CrownGold = styled(Crown)`
  fill: #f1c21b;
`;

interface MemberCellParamsWithTranslate extends MemberCellRendererParams {
  translate: Translate;
}

interface MemberCellParamsForMoreActions extends MemberCellRendererParams {
  project: Project;
  loggedInUserPermissions: Membership | undefined;
  membersMeta: MembersMeta | undefined;
  loggedInUser: UserProfileInfo;
  memberships: Memberships;
}

interface MemberCellParamsWithMembership extends MemberCellRendererParams {
  memberships: Memberships;
}

export const getPermission = ({
  data,
}: MemberCellParamsWithTranslate): JSX.Element | null => {
  const permission = data?.permissionSet?.permissionSetName ?? '';

  return (
    <SecondaryText
      data-cy={`settings.team_members.grid.permission.${data.user.userId}`}
    >
      {permission ?? <EmptyCell />}
    </SecondaryText>
  );
};

export const TeamMemberOptionsRenderer = ({
  showMemberDetail,
  data,
  openCostRateManager,
}) => {
  return (
    <TeamMembersMoreActionsMenu
      member={data}
      showMemberDetail={showMemberDetail}
      openCostRateManager={openCostRateManager}
    />
  );
};

export const MoreOptionsCellRendererForGroupUsers = ({
  data,
  deleteGroupMember,
  groupId,
  isDisabled,
}) => {
  const translate = useTranslate();
  const menu = (
    <Menu>
      <Menu.ItemDelete
        icon={<></>}
        key="remove"
        onClick={() =>
          deleteGroupMember(
            {
              groupId,
              data: { users: [{ emailId: data.user.emailId }] },
            },
            {
              onSuccess: () => {
                toastService({
                  title: translate('settings.toast.groups.update_group_title'),
                  message: translate(
                    'settings.toast.groups.update_group_description',
                  ),
                });
              },
              onError: () => {
                toastService({
                  title: translate(
                    'settings.toast.error.groups.update_group_title',
                  ),
                  message: translate(
                    'settings.toast.error.groups.update_group_description',
                  ),
                });
              },
            },
          )
        }
        disabled={isDisabled}
      >
        <FormattedMessage id="settings.groups.remove_member_in_group" />
      </Menu.ItemDelete>
    </Menu>
  );

  return <MoreActionsDropdown menu={menu} />;
};

export const GetUserName = ({
  data,
}: MemberCellRendererParams): JSX.Element | null => {
  return data ? (
    <ClickableName>
      <AvatarWrapper>
        <Avatar name={fullName(data.user)} src={data.user?.photoUrl} />
      </AvatarWrapper>
      <SecondaryText
        data-cy={`settings.team_members.grid.name.${data.user.userId}`}
      >
        <Tooltip
          title={fullName(data.user).length > 25 ? fullName(data.user) : ''}
        >
          <NameWrapper>{fullName(data.user)}</NameWrapper>
        </Tooltip>
      </SecondaryText>
    </ClickableName>
  ) : null;
};

const DeactivatedText = styled.span`
  color: var(--red-60);
`;

const InvitedText = styled.span`
  color: var(--blue-60);
`;

export const GetUserNameWithProjectOwnerAndTimeOffIcons = ({
  data: userData,
  projectId,
}): JSX.Element | null => {
  const data = userData as UserData;
  const translate = useTranslate();

  const status = data
    ? data?.userStatus === UserStatus.Inactive
      ? translate('deactivated')
      : translate('invited')
    : '';

  const { data: project } = useProjectDetail(ProjectId(projectId));
  const projectOwnerId = project?.projectOwner?.userId;

  const users = useGetAllNativeUsersFromIndexedDb();
  const currentUser = useMemo(
    () => users.find((user) => user.userId === data?.userId),
    [users, data?.userId],
  );

  const hasTimeOffToday = checkIfCurrentDateHasTimeOff(
    currentUser?.userTimeOffDetails ?? [],
  );

  const timeOffText = (
    <GetTimeOffTooltipContent
      timeOff={hasTimeOffToday}
      userName={(currentUser as any)?.userName}
    />
  );

  return data ? (
    <ClickableName>
      <AvatarWrapper>
        <Avatar name={fullName(data)} src={data?.photoUrl} />
        {projectOwnerId === data.userId && (
          <Tooltip
            title={<FormattedMessage id="projects.list.project_owner" />}
            placement="right"
          >
            <span
              className="icon"
              style={hasTimeOffToday ? { marginTop: '3px' } : {}}
            >
              <CrownGold />
            </span>
          </Tooltip>
        )}
        {hasTimeOffToday && (
          <Tooltip title={timeOffText} placement="right">
            <SailboatOffshore16 color="var(--cp-purple-60)" />
          </Tooltip>
        )}
      </AvatarWrapper>
      <SecondaryText data-cy={`settings.team_members.grid.name.${data.userId}`}>
        <Tooltip title={fullName(data).length > 25 ? fullName(data) : ''}>
          <NameWrapper>{fullName(data)}</NameWrapper>
        </Tooltip>
        {data.userStatus === UserStatus.Inactive && (
          <DeactivatedText>{status}</DeactivatedText>
        )}
        {data.userStatus === UserStatus.Invited && (
          <InvitedText>{status}</InvitedText>
        )}
      </SecondaryText>
    </ClickableName>
  ) : null;
};

export const NoResultTeamMemberEmptyState: FC = () => {
  return (
    <EmptyStateContainer>
      <EmptyState
        image={<EmptyStateIcon icon={faTelescope} />}
        title={
          <FormattedMessage id="settings.team_members.empty.state.title" />
        }
        message={
          <FormattedMessage id="settings.team_members.empty.state.message" />
        }
        secondaryCTA={null}
      />
    </EmptyStateContainer>
  );
};

export const GetCostRate: FC<MemberCellParamsWithTranslate> = ({
  data,
  value,
}) => {
  const currencyMap = useAccountCurrency();

  if (currencyMap == null || !value) {
    return <EmptyCell />;
  }

  return (
    <SecondaryText>
      {currencyRenderer(value, data.costRate?.currency ?? currencyMap.default)}
    </SecondaryText>
  );
};

export const Email = ({ value }) => {
  return (
    <MailContainer>
      <MailWrapper>{value}</MailWrapper>
      <Button type="text" size="small">
        <StyledText
          copyable={{
            text: value,
            icon: [<CopyIcon />, <CopySuccessIcon />],
            tooltips: [
              <FormattedMessage id="click_to_copy" />,
              <FormattedMessage id="copied" />,
            ],
          }}
        />
      </Button>
    </MailContainer>
  );
};

export const MoreActionsCellRenderer: React.FC<MemberCellParamsForMoreActions> =
  ({
    data,
    project,
    loggedInUserPermissions,
    membersMeta,
    loggedInUser,
    memberships,
  }) => {
    return (
      <MoreActions
        projectId={project.projectId}
        projectOwner={project.projectOwner}
        user={data.user}
        loggedInUserPermissions={loggedInUserPermissions}
        membersMeta={membersMeta}
        loggedInUser={loggedInUser}
        memberships={memberships}
      />
    );
  };

export const Status: FC<MemberCellParamsWithMembership> = ({
  data,
  memberships,
}) => {
  const userData = data as UserData;
  const status = userData?.userStatus;
  const user = userData?.user;

  const membership = memberships.byUserId[user?.userId];

  if (membership && membership.joinedAt) {
    return (
      <Text>
        <FormattedMessage
          id="invitation.joined_on"
          values={{ date: moment(membership.joinedAt).format('DD MMM') }}
        />
      </Text>
    );
  }

  if (status === UserStatus.Inactive) {
    return (
      <Text>
        <FormattedMessage id="Deactivated" />
      </Text>
    );
  }

  if (status === UserStatus.Invited) {
    return (
      <PendingStatusText>
        <FormattedMessage id="invitation.pending" />
      </PendingStatusText>
    );
  }

  return <EmptyCell />;
};
